import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, BookOpen, Users, Star, TrendingUp, Eye, Edit, Trash2, BookMarked, Loader, AlertTriangle, Circle, ChevronLeft, ChevronRight } from 'lucide-react';
import './CourseListTab.css';
import {
  getAllCourses,
  getCourseStats,
  deleteCourse,
  updateCourseStatus,
  type CourseListItem,
  type CourseStats,
} from '../../../services/AdminService/AdminCourseManagementService';
import CourseStatusModal from '../../../Components/CourseManagement/CourseStatusModal';

interface Course {
  id: number;
  title: string;
  category: string;
  description: string;
  instructor: string;
  students: number;
  modules: number;
  rating: number;
  completion: number;
  status: 'Active' | 'Inactive' | 'Draft' | 'Pending';
  updatedAt: string;
  thumbnail?: string;
  level?: string;
}

interface CourseListTabProps {
  onEditCourse?: (courseId: number) => void;
}

const CourseListTab: React.FC<CourseListTabProps> = ({ onEditCourse }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [courses, setCourses] = useState<Course[]>([]);
  const [stats, setStats] = useState<CourseStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  // Load courses and stats on mount
  useEffect(() => {
    loadCourses();
    loadStats();
  }, []);

  const loadCourses = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get ALL courses (is_deleted = 0, no status filter)
      const result = await getAllCourses();
      const allCourses = result.data || [];

      // Transform API data to component format
      const transformedCourses: Course[] = allCourses.map((course) => {
        // Map API status to display status
        let displayStatus: 'Active' | 'Inactive' | 'Draft' | 'Pending' = 'Pending';
        const apiStatus = course.status?.toLowerCase();

        if (apiStatus === 'active' || apiStatus === 'published') {
          displayStatus = 'Active';
        } else if (apiStatus === 'inactive') {
          displayStatus = 'Inactive';
        } else if (apiStatus === 'draft') {
          displayStatus = 'Draft';
        } else if (apiStatus === 'pending') {
          displayStatus = 'Pending';
        }

        return {
          id: course.id,
          title: course.title,
          category: 'General', // You can add category mapping if needed
          description: course.shortDescription || 'No description available',
          instructor: 'Instructor', // Can be fetched from course creator if needed
          students: course.enrollmentCount || 0,
          modules: course.lessonCount || 0,
          rating: course.avgRating || 0,
          completion: 0, // Not calculated for individual courses
          status: displayStatus,
          updatedAt: formatDate(course.updatedAt || course.createdAt),
          thumbnail: course.thumbnail,
          level: course.level,
        };
      });

      setCourses(transformedCourses);
    } catch (err: any) {
      console.error('Failed to load courses:', err);
      setError('Failed to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await getCourseStats();
      if (result.success && result.data) {
        setStats(result.data);
      }
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const handleDelete = async (courseId: number) => {
    if (!window.confirm('Are you sure you want to delete this course?')) {
      return;
    }

    try {
      const result = await deleteCourse(courseId);
      if (result.success) {
        // Reload courses and stats after deletion
        await loadCourses();
        await loadStats();
      } else {
        alert(result.error?.message || 'Failed to delete course');
      }
    } catch (err: any) {
      alert('Failed to delete course: ' + err.message);
    }
  };

  const handleEdit = (courseId: number) => {
    // Call the parent handler to switch to edit mode
    if (onEditCourse) {
      onEditCourse(courseId);
    } else {
      console.log('Edit course:', courseId);
      alert(`Edit functionality for course ${courseId} is not available.`);
    }
  };

  const handleView = (courseId: number) => {
    // Navigate to admin course view page
    navigate(`/admin/course/${courseId}`, {
      state: { sourceLabel: 'Course Management' }
    });
  };

  const handleOpenStatusModal = (course: Course) => {
    setSelectedCourse(course);
    setIsStatusModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    setIsStatusModalOpen(false);
    setSelectedCourse(null);
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!selectedCourse) return;

    setIsUpdatingStatus(true);
    try {
      const result = await updateCourseStatus(
        selectedCourse.id,
        newStatus as 'active' | 'inactive' | 'pending'
      );

      if (result.success) {
        // Update the local state to reflect the new status
        setCourses((prevCourses) =>
          prevCourses.map((course) =>
            course.id === selectedCourse.id
              ? {
                  ...course,
                  status: (newStatus.charAt(0).toUpperCase() + newStatus.slice(1)) as Course['status'],
                }
              : course
          )
        );
        handleCloseStatusModal();
        // Reload stats since status change affects active course count
        await loadStats();
      } else {
        alert(result.error?.message || 'Failed to update course status');
      }
    } catch (err: any) {
      alert('Failed to update course status: ' + err.message);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Filter courses based on search and status
  const filteredCourses = courses.filter((course) => {
    const matchesSearch =
      searchQuery === '' ||
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.description.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      selectedStatus === 'all' ||
      (selectedStatus === 'active' && course.status === 'Active') ||
      (selectedStatus === 'draft' && course.status === 'Draft') ||
      (selectedStatus === 'pending' && course.status === 'Pending');

    return matchesSearch && matchesStatus;
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedStatus]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredCourses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCourses = filteredCourses.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
  };

  // Use stats from API
  const statsCards = [
    {
      label: 'Total Courses',
      value: stats?.activeCourses.toString() || '0',
      change: 'Active courses',
      icon: BookOpen,
      color: '#FF8811'
    },
    {
      label: 'Total Learners',
      value: stats?.totalLearners.toString() || '0',
      change: 'Active & Pending',
      icon: Users,
      color: '#00A045'
    },
    {
      label: 'Avg. Rating',
      value: stats?.avgRating ? stats.avgRating.toFixed(1) : '0.0',
      change: `${stats?.totalRatings || 0} reviews`,
      icon: Star,
      color: '#FACC15'
    },
    {
      label: 'Avg. Completion',
      value: `${stats?.avgCompletion ? Math.round(stats.avgCompletion) : 0}%`,
      change: 'All courses',
      icon: TrendingUp,
      color: '#8200DB'
    }
  ];

  return (
    <>
      {/* Error Message */}
      {error && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}>
          <AlertTriangle size={20} color="#dc2626" />
          <span style={{ color: '#dc2626' }}>{error}</span>
        </div>
      )}

      <div className="trainer-coursemanagement-stats-grid">
        {statsCards.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <div key={index} className="trainer-coursemanagement-stat-card">
              <div className="trainer-coursemanagement-stat-content">
                <div className="trainer-coursemanagement-stat-label">{stat.label}</div>
                <div className="trainer-coursemanagement-stat-value">{stat.value}</div>
                <div className="trainer-coursemanagement-stat-change">{stat.change}</div>
              </div>
              <div className="trainer-coursemanagement-stat-icon" style={{ backgroundColor: stat.color }}>
                <IconComponent size={24} color="white" />
              </div>
            </div>
          );
        })}
      </div>

      <div className="trainer-coursemanagement-filters">
        <div className="trainer-coursemanagement-search-box">
          <Search className="trainer-coursemanagement-search-icon" size={20} />
          <input
            type="text"
            placeholder="Search courses by title or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          className="trainer-coursemanagement-filter-select"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="active">Active/Published</option>
          <option value="draft">Draft</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '4rem',
          flexDirection: 'column',
          gap: '1rem',
        }}>
          <Loader size={48} className="spinner" color="#FF8C00" />
          <p style={{ color: '#6b7280' }}>Loading courses...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredCourses.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '4rem',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '2px dashed #d1d5db',
        }}>
          <BookOpen size={64} color="#9ca3af" style={{ margin: '0 auto 1rem' }} />
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
            No courses found
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            {searchQuery
              ? 'Try adjusting your search or filters'
              : 'Create your first course to get started'}
          </p>
        </div>
      )}

      {/* Courses Grid */}
      {!loading && filteredCourses.length > 0 && (
        <>
        <div className="trainer-coursemanagement-courses-grid">
          {paginatedCourses.map((course) => (
          <div key={course.id} className="trainer-coursemanagement-course-card">
            <div className="trainer-coursemanagement-course-card-header">
              <div className="trainer-coursemanagement-course-icon-wrapper">
                <BookMarked size={48} strokeWidth={1.5} color="#9ca3af" />
              </div>
              <span className={`trainer-coursemanagement-status-badge trainer-coursemanagement-status-${course.status.toLowerCase()}`}>
                {course.status}
              </span>
            </div>

            <h3 className="trainer-coursemanagement-course-title">{course.title}</h3>

            <span className={`trainer-coursemanagement-category-badge trainer-coursemanagement-category-${course.category.toLowerCase()}`}>
              {course.category}
            </span>

            <p className="trainer-coursemanagement-course-description">{course.description}</p>

            <div className="trainer-coursemanagement-course-instructor">by {course.instructor}</div>

            <div className="trainer-coursemanagement-course-stats">
              <div className="trainer-coursemanagement-stat-item">
                <Users className="trainer-coursemanagement-stat-icon-small" size={16} />
                <span>{course.students}</span>
              </div>
              <div className="trainer-coursemanagement-stat-item">
                <BookOpen className="trainer-coursemanagement-stat-icon-small" size={16} />
                <span>{course.modules} modules</span>
              </div>
            </div>

            <div className="trainer-coursemanagement-course-metrics">
              <div className="trainer-coursemanagement-metric">
                <Star className="trainer-coursemanagement-metric-icon" size={16} fill="#FACC15" color="#FACC15" />
                <span>{course.rating}</span>
              </div>
              <div className="trainer-coursemanagement-metric">
                <TrendingUp className="trainer-coursemanagement-metric-icon" size={16} color="#00A045" />
                <span>{course.completion}%</span>
              </div>
            </div>

            <div className="trainer-coursemanagement-course-actions">
              <button
                className="trainer-coursemanagement-action-btn trainer-coursemanagement-action-view"
                onClick={() => handleView(course.id)}
              >
                <Eye size={16} /> View
              </button>
              <button
                className="trainer-coursemanagement-action-btn trainer-coursemanagement-action-edit"
                onClick={() => handleEdit(course.id)}
              >
                <Edit size={16} /> Edit
              </button>
              <button
                className="trainer-coursemanagement-action-btn trainer-coursemanagement-action-icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenStatusModal(course);
                }}
                title="Change status"
                style={{
                  border: `2px solid ${
                    course.status === 'Active' ? '#00A045' :
                    course.status === 'Pending' ? '#FACC15' :
                    '#EF4444'
                  }`,
                  color: course.status === 'Active' ? '#00A045' :
                         course.status === 'Pending' ? '#FACC15' :
                         '#EF4444'
                }}
              >
                <Circle
                  size={16}
                  fill={course.status === 'Active' ? '#00A045' :
                        course.status === 'Pending' ? '#FACC15' :
                        '#EF4444'}
                />
              </button>
              <button
                className="trainer-coursemanagement-action-btn trainer-coursemanagement-action-icon-btn"
                onClick={() => handleDelete(course.id)}
                title="Delete course"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="trainer-coursemanagement-course-updated">Updated {course.updatedAt}</div>
          </div>
          ))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '2rem',
            padding: '1rem',
            backgroundColor: 'var(--color-card-bg)',
            borderRadius: '12px',
            border: '1px solid var(--color-border)',
          }}>
            <div style={{ color: 'var(--color-secondary-text)', fontSize: '14px' }}>
              Showing {startIndex + 1}-{Math.min(endIndex, filteredCourses.length)} of {filteredCourses.length} courses
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  backgroundColor: currentPage === 1 ? 'var(--color-greyish-bg)' : 'var(--color-card-bg)',
                  color: currentPage === 1 ? 'var(--color-secondary-text)' : 'var(--color-black-text)',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                }}
              >
                <ChevronLeft size={16} /> Previous
              </button>

              {/* Page Numbers */}
              <div style={{ display: 'flex', gap: '4px' }}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  const showPage =
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1);

                  // Show ellipsis
                  const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
                  const showEllipsisAfter = page === currentPage + 2 && currentPage < totalPages - 2;

                  if (showEllipsisBefore || showEllipsisAfter) {
                    return (
                      <span key={page} style={{ padding: '8px 4px', color: 'var(--color-secondary-text)' }}>
                        ...
                      </span>
                    );
                  }

                  if (!showPage) return null;

                  return (
                    <button
                      key={page}
                      onClick={() => handlePageClick(page)}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px',
                        backgroundColor: currentPage === page ? 'var(--color-sunflare)' : 'var(--color-card-bg)',
                        color: currentPage === page ? 'white' : 'var(--color-black-text)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: currentPage === page ? '600' : '500',
                        minWidth: '40px',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (currentPage !== page) {
                          e.currentTarget.style.backgroundColor = 'var(--color-light-orange-bg)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentPage !== page) {
                          e.currentTarget.style.backgroundColor = 'var(--color-card-bg)';
                        }
                      }}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  backgroundColor: currentPage === totalPages ? 'var(--color-greyish-bg)' : 'var(--color-card-bg)',
                  color: currentPage === totalPages ? 'var(--color-secondary-text)' : 'var(--color-black-text)',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                }}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
        </>
      )}

      {/* Course Status Modal */}
      {selectedCourse && (
        <CourseStatusModal
          isOpen={isStatusModalOpen}
          onClose={handleCloseStatusModal}
          onConfirm={handleStatusChange}
          currentStatus={selectedCourse.status.toLowerCase()}
          courseName={selectedCourse.title}
          isLoading={isUpdatingStatus}
        />
      )}
    </>
  );
};

export default CourseListTab;